import {
  Box,
  Paper,
  Typography,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  TablePagination,
} from '@mui/material';
import React, { ReactElement, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { useNavigate } from 'react-router-dom';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';
import { IRiskAssesmentPagination } from 'state/pagination/Reducer';

interface TemplateListProps {
  templates?: any[];
  onMarkInactive: (id: string) => void;

  riskAssessmentPagination: IRiskAssesmentPagination;
  handlePageChange: (e: any, data: any) => void;
  handleRowsPerPageChange: (data: any) => void;
}

const TemplateList = ({
  templates,
  onMarkInactive,
  riskAssessmentPagination,
  handlePageChange,
  handleRowsPerPageChange,
}: TemplateListProps): ReactElement => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const open = Boolean(anchorEl);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    template: any
  ): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
    setSelectedTemplate(null);
  };

  const handleEdit = (): void => {
    if (selectedTemplate) {
      navigate(`/assessment-templates/edit/${selectedTemplate._id}`);
    }
    handleMenuClose();
  };

  const handleMarkInactive = (id: string): void => {
    onMarkInactive(id);
    handleMenuClose();
  };

  return (
    <div>
      {templates &&
        templates.map((template, idx) => (
          <Box key={idx} mb={2}>
            <Paper
              variant="outlined"
              sx={{
                p: 5,
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        fontSize="1.1rem"
                        className="word-break"
                      >
                        {template.templateName}
                      </Typography>
                      <Chip
                        label={template.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        className="mx-2"
                        sx={{ background: 'black', color: 'white' }}
                      />
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Version: {template.version}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(event) => handleMenuClick(event, template)}
                        aria-controls={open ? 'template-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" my={1}>
                    {template.description || 'No description provided'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Typography variant="body1" fontWeight={600}>
                      Applicable to:
                    </Typography>
                    <Chip
                      label={template.scope}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        ))}

      <Menu
        anchorEl={anchorEl}
        id="template-menu"
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              minWidth: 180,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {orgACLConfig.riskManagement?.includes(
          'riskManagement.assessmentTemplates.editTemplate'
        ) && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit
          </MenuItem>
        )}
        {orgACLConfig.riskManagement?.includes(
          'riskManagement.assessmentTemplates.markAsInactive'
        ) && (
          <MenuItem onClick={() => handleMarkInactive(selectedTemplate._id)}>
            <ListItemIcon>
              <BlockIcon fontSize="small" />
            </ListItemIcon>
            Mark as inactive
          </MenuItem>
        )}
      </Menu>
      {templates && templates?.length > 0 && (
        <TablePagination
          component="div"
          count={riskAssessmentPagination.totalRecords}
          page={riskAssessmentPagination.offset - 1}
          rowsPerPage={riskAssessmentPagination.pageLimit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[1, 10, 20, 50]}
        />
      )}
    </div>
  );
};

export default TemplateList;
